import { useMemo, useState, type ReactElement } from 'react';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { useDashboardAlerts, type DashboardAlert } from '@/lib/dashboard';

/**
 * Banner condicional con alertas activas del dashboard (M2.16).
 *
 * - Llama `useDashboardAlerts` y filtra las que el usuario haya descartado
 *   localmente para el día de hoy (`localStorage`, key
 *   `tm:alerts:dismissed:${id}:${todayISO}`).
 * - Si no hay alertas activas → NO renderiza nada (no muestra el contenedor
 *   vacío para no robar espacio).
 * - Orden visual: danger primero, warn segundo, info tercero. Dentro de
 *   cada nivel se respeta el orden del backend.
 *
 * Decisión sobre dismiss:
 *  - Key con la fecha de hoy → al cambiar de día las alertas vuelven a
 *    aparecer automáticamente (no hace falta limpiar nada). Esto encaja con
 *    el contrato ("dismiss por 24h").
 *  - Si `localStorage` falla (modo privado raro), se ignoran las
 *    dismisseadas — la alerta se vuelve a mostrar. Mejor verla que
 *    perderla.
 */
export function AlertsBanner(): ReactElement | null {
  const query = useDashboardAlerts();
  const [tick, setTick] = useState(0);

  const visible = useMemo<DashboardAlert[]>(() => {
    if (!query.data) return [];
    const todayISO = todayKey();
    const ordered = [...query.data.alerts].sort(
      (a, b) => severityRank(a.severity) - severityRank(b.severity),
    );
    return ordered.filter((alert) => !isDismissed(alert.id, todayISO));
    // `tick` fuerza re-evaluación tras dismiss.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, tick]);

  if (visible.length === 0) return null;

  function dismiss(id: string) {
    markDismissed(id, todayKey());
    setTick((n) => n + 1);
  }

  return (
    <section
      data-testid="alerts-banner"
      aria-label="Alertas activas"
      className="space-y-2"
    >
      {visible.map((alert) => (
        <AlertCard key={alert.id} alert={alert} onDismiss={() => dismiss(alert.id)} />
      ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Alert card
// ---------------------------------------------------------------------------

function AlertCard({
  alert,
  onDismiss,
}: {
  alert: DashboardAlert;
  onDismiss: () => void;
}): ReactElement {
  const style = styleFor(alert.severity);
  const Icon = style.Icon;
  return (
    <article
      role="alert"
      data-testid={`alerts-banner-item-${alert.id}`}
      data-severity={alert.severity}
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 shadow-sm ${style.className}`}
    >
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{alert.title}</p>
        <p className="mt-0.5 text-xs leading-snug">{alert.detail}</p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={`Descartar alerta: ${alert.title}`}
        className={`-mr-1 -mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-base font-semibold opacity-70 transition hover:bg-black/5 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 ${style.focusRing}`}
      >
        <span aria-hidden="true">×</span>
      </button>
    </article>
  );
}

function styleFor(severity: DashboardAlert['severity']): {
  className: string;
  focusRing: string;
  Icon: typeof AlertCircle;
} {
  switch (severity) {
    case 'danger':
      return {
        className: 'border-rose-200 bg-rose-50 text-rose-800',
        focusRing: 'focus:ring-rose-600',
        Icon: AlertCircle,
      };
    case 'warn':
      return {
        className: 'border-amber-200 bg-amber-50 text-amber-800',
        focusRing: 'focus:ring-amber-600',
        Icon: AlertTriangle,
      };
    case 'info':
      return {
        className: 'border-sky-200 bg-sky-50 text-sky-800',
        focusRing: 'focus:ring-sky-600',
        Icon: Info,
      };
  }
}

function severityRank(s: DashboardAlert['severity']): number {
  switch (s) {
    case 'danger': return 0;
    case 'warn': return 1;
    case 'info': return 2;
  }
}

// ---------------------------------------------------------------------------
// Dismiss persistence
// ---------------------------------------------------------------------------

/** Devuelve la fecha de hoy como `YYYY-MM-DD` (UTC). */
function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export function dismissKey(id: string, todayISO: string): string {
  return `tm:alerts:dismissed:${id}:${todayISO}`;
}

function isDismissed(id: string, todayISO: string): boolean {
  try {
    return globalThis.localStorage?.getItem(dismissKey(id, todayISO)) === '1';
  } catch {
    return false;
  }
}

function markDismissed(id: string, todayISO: string): void {
  try {
    globalThis.localStorage?.setItem(dismissKey(id, todayISO), '1');
  } catch {
    // Modo privado / quota exceeded — silenciar; la alerta volverá a
    // aparecer al siguiente render, pero al menos no rompemos la UI.
  }
}
